import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  GridDisplay,
  JustifyContent,
  Link,
  LinkVariant,
  Logo,
  LogoVariant,
  Text,
} from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { helpPreLoginVisibilityChange } from 'actions/login'
import { trackNavigationClick } from 'actions/tracking'
import * as trackingKeys from 'actions/trackingKeys'
import { client as clientRoutes } from 'config/routes'
import { getIsAuthenticated } from 'selectors/auth'

import { AppStoreBadges } from './AppStoreBadges'
import { FooterColumn } from './FooterColumn'
import { FooterLink } from './FooterLink'
import { FooterNotice } from './FooterNotice'
import UKTaxStrategyPdf from './assets/UK_Tax_Strategy.pdf'
import facebookIcon from './assets/social-facebook.svg'
import instagramIcon from './assets/social-instagram.svg'
import twitterIcon from './assets/social-twitter.svg'
import youtubeIcon from './assets/social-youtube.svg'
import { COMPANY, OUR_RECIPES, RECIPE_BOXES } from './footerColumns'

const TAGLINE = `The UK's best value recipe box. Great-tasting recipes, ready in as little as 10 mins.`

const SOCIAL_LINKS = [
  {
    src: facebookIcon,
    label: 'Facebook',
    href: 'http://www.facebook.com/goustocooking',
    testid: 'footer-facebook',
  },
  {
    src: twitterIcon,
    label: 'Twitter',
    href: 'https://twitter.com/goustocooking',
    testid: 'footer-twitter',
  },
  {
    src: youtubeIcon,
    label: 'YouTube',
    href: 'https://www.youtube.com/UKGousto/',
    testid: 'footer-youtube',
  },
  {
    src: instagramIcon,
    label: 'Instagram',
    href: 'http://instagram.com/goustocooking',
    testid: 'footer-instagram',
  },
] as const

type FooterProps = {
  copyright?: boolean
  simple?: boolean
  isOnLandingPage?: boolean
}

export const Footer = ({
  copyright = true,
  simple = false,
  isOnLandingPage = false,
}: FooterProps) => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(getIsAuthenticated)
  const simpleHeader = useSelector((state: any) => state.persist?.get('simpleHeader', false))
  const effectiveSimple = Boolean(simple || simpleHeader)

  const track = (trackingData: Record<string, unknown>) =>
    dispatch(trackNavigationClick(trackingData))

  const renderDataLinks = (links: typeof RECIPE_BOXES) =>
    links.map(({ label, href, testid, trackingActionType }) => (
      <FooterLink
        key={testid}
        href={href}
        testid={testid}
        onClick={trackingActionType ? () => track({ actionType: trackingActionType }) : undefined}
      >
        {label}
      </FooterLink>
    ))

  if (effectiveSimple) {
    return (
      <footer>
        <Box
          paddingV={6}
          paddingH={[4, null, null, 8]}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={4}
        >
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={2}
            alignItems={AlignItems.FlexStart}
          >
            <Text size={[2, null, null, 4]}>
              Terms (
              <Link href={clientRoutes.termsOfUse} data-testid="footer-terms-of-use">
                Website
              </Link>
              ) (
              <Link
                href={clientRoutes.termsAndConditions}
                data-testid="footer-terms-and-conditions"
              >
                Sale
              </Link>
              )
            </Text>
            <Link href={clientRoutes.privacyCookiesPolicies} data-testid="footer-privacy-statement">
              Privacy and Cookies Policies
            </Link>
            <Link href={clientRoutes.modernSlavery} data-testid="footer-modern-slavery-statement">
              Modern Slavery Statement
            </Link>
          </Box>
          {copyright ? (
            <Box id="copyright">
              <Text size={[2, null, null, 4]}>
                © Gousto {new Date().getFullYear()}. All rights reserved.
              </Text>
            </Box>
          ) : null}
        </Box>
      </footer>
    )
  }

  const termsItem = (
    <Text size={[2, null, null, 4]} color={Color.White}>
      Terms (
      <FooterLink
        href={clientRoutes.termsOfUse}
        testid="footer-terms-of-use"
        title="Terms &amp; Conditions"
      >
        Website
      </FooterLink>
      ) (
      <FooterLink
        href={clientRoutes.termsAndConditions}
        testid="footer-terms-and-conditions"
        title="Terms &amp; Conditions for Sale of Goods"
      >
        Sale
      </FooterLink>
      )
    </Text>
  )

  const privacyItem = (
    <FooterLink
      href={clientRoutes.privacyCookiesPolicies}
      testid="footer-privacy-statement"
      title="Privacy Policy"
    >
      Privacy and Cookies Policies
    </FooterLink>
  )

  const modernSlaveryItem = (
    <FooterLink
      href={clientRoutes.modernSlavery}
      testid="footer-modern-slavery-statement"
      title="Modern Slavery"
    >
      Modern Slavery Statement
    </FooterLink>
  )

  const helpItem = isAuthenticated ? (
    <FooterLink
      href={clientRoutes.helpCentre}
      testid="footer-learn-more"
      optimizely="footer-help-link"
      title="Help"
      onClick={() =>
        track({ actionType: trackingKeys.clickHelpFooter, seCategory: 'help', logged_in: true })
      }
    >
      Help
    </FooterLink>
  ) : (
    <FooterLink
      testid="footer-help"
      title="Help"
      onClick={() => {
        dispatch(helpPreLoginVisibilityChange(true))
        track({ actionType: trackingKeys.clickHelpFooter, seCategory: 'help', logged_in: false })
      }}
    >
      Help
    </FooterLink>
  )

  const copyrightBlock = copyright ? (
    <Box id="copyright">
      <Text size={[2, null, null, 4]} color={Color.White}>
        © Gousto {new Date().getFullYear()}. All rights reserved.
      </Text>
    </Box>
  ) : null

  return (
    <footer>
      <Box
        bg={Color.ColdGrey_600}
        color={Color.White}
        // -1px overlap hides the sub-pixel seam where the white body shows between a dark section and the footer.
        style={{ marginTop: '-1px' }}
        paddingV={[14, null, null, 20]}
        paddingH={[4, null, null, 8]}
        display={Display.Flex}
        justifyContent={JustifyContent.Center}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={10}
          maxWidth="1013px"
          width="100%"
        >
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={6}
            alignItems={AlignItems.Center}
          >
            <Box display={Display.Flex} gap={4} justifyContent={JustifyContent.Center}>
              {SOCIAL_LINKS.map(({ src, label, href, testid }) => (
                <Link
                  key={testid}
                  href={href}
                  variant={LinkVariant.None}
                  aria-label={label}
                  data-testid={testid}
                >
                  <img src={src} alt="" width={24} height={24} />
                </Link>
              ))}
            </Box>
            <AppStoreBadges />
          </Box>

          <Box
            display={Display.Flex}
            flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
            gap={[8, null, null, 16]}
            alignItems={AlignItems.FlexStart}
            width="100%"
          >
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              alignItems={AlignItems.FlexStart}
              gap={4}
              minWidth={['auto', null, null, '200px']}
            >
              <Link
                href={clientRoutes.home}
                variant={LinkVariant.None}
                aria-label="Gousto homepage"
                data-testid="footer-home"
              >
                <Logo logoVariant={LogoVariant.Inherit} size={8} />
              </Link>
              <Text size={4} color={Color.White}>
                {TAGLINE}
              </Text>
            </Box>

            <FooterColumn heading="Recipe Boxes">
              <Box
                display={Display.Flex}
                flexDirection={[FlexDirection.Row, null, null, FlexDirection.Column]}
                gap={[4, null, null, 2]}
              >
                <Box
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  alignItems={AlignItems.FlexStart}
                  gap={2}
                  width={['164px', null, null, '200px']}
                  minWidth="0"
                  flexShrink={1}
                >
                  {renderDataLinks(RECIPE_BOXES.slice(0, 6))}
                </Box>
                <Box
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  alignItems={AlignItems.FlexStart}
                  gap={2}
                  width={['164px', null, null, '200px']}
                  minWidth="0"
                  flexShrink={1}
                >
                  {renderDataLinks(RECIPE_BOXES.slice(6))}
                </Box>
              </Box>
            </FooterColumn>

            <Box
              display={[Display.Flex, null, null, GridDisplay.Grid]}
              flexDirection={FlexDirection.Column}
              rowGap={[8, null, null, 14]}
              columnGap={[null, null, null, 16]}
              gridTemplateColumns={[null, null, null, 'repeat(2, max-content)']}
            >
              <FooterColumn
                heading={OUR_RECIPES.heading}
                gridColumn={[null, null, null, '1']}
                gridRow={[null, null, null, '1']}
              >
                {renderDataLinks(OUR_RECIPES.links)}
              </FooterColumn>
              <FooterColumn
                heading={COMPANY.heading}
                gridColumn={[null, null, null, '1']}
                gridRow={[null, null, null, '2']}
              >
                {renderDataLinks(COMPANY.links)}
              </FooterColumn>
              <FooterColumn
                heading="Help and Support"
                gridColumn={[null, null, null, '2']}
                gridRow={[null, null, null, '1']}
              >
                {helpItem}
                <FooterLink
                  href={clientRoutes.accessibilityStatement}
                  testid="footer-accessibility-statement"
                  title="Accessibility Statement"
                >
                  Accessibility Statement
                </FooterLink>
                <FooterLink
                  href={clientRoutes.studentDiscount}
                  testid="footer-student-discount"
                  title="Student Discount"
                >
                  Student discount
                </FooterLink>
                <FooterLink
                  href={clientRoutes.affiliates}
                  testid="footer-affiliates"
                  title="Affiliates"
                >
                  Affiliates
                </FooterLink>
              </FooterColumn>
              <FooterColumn
                heading="Legal and Compliance"
                gridColumn={[null, null, null, '2']}
                gridRow={[null, null, null, '2']}
              >
                {termsItem}
                {privacyItem}
                {modernSlaveryItem}
                <FooterLink
                  href={UKTaxStrategyPdf}
                  testid="footer-uk-tax-strategy"
                  title="UK Tax Strategy"
                >
                  UK Tax Strategy
                </FooterLink>
              </FooterColumn>
            </Box>
          </Box>

          {isOnLandingPage ? <FooterNotice /> : null}
          {copyrightBlock}
        </Box>
      </Box>
    </footer>
  )
}
